/* eslint one-var: "off" */
import axios from 'axios';
import { remove } from '../utilities/utilities';
import Choices from 'choices.js';

/**
 * EpiserverForm
 * A plugin that handles Episerver forms in an accessible way.
 * @version 1.0.0
 * @exports episerverForm
 */
export default class EpiserverForm {
    /**
     * Constructor
     * @param {Object} el - A DOM node.
     * @public
     */
    constructor(el) {
        /** Placeholder for form node. */
        this.formNode = el;

        /** Boolean to indicate if there are any ajax-request in progress. */
        this.requestInProgress = false;

        this._init();
    }

    get invalidFieldClass() { return 'form__field--invalid' }
    get invalidLabelClass() { return 'form__label--invalid' }

    /**
     * The initialization function for this module.
     * @public
     */
    _init() {
        this.formMessageNode = this.formNode.querySelector('.form__message');
        this.fieldHolderNode = this.formNode.querySelector('.form__fields-holder');

        this._initialFocus();
        this._initMultipleSelects();
        this._initEvents();
    }

    /**
     * Function that sets focus to the form__status container on load.
     * @private
     */
    _initialFocus() {
        const statusNode = this.formNode.querySelector('.form__status');

        if (statusNode) {
            statusNode.focus();
        }
    }

    /**
     * Initiates multiple selects with Choices.js
     * @private
     */
    _initMultipleSelects() {
        var multipleSelects = this.formNode.querySelectorAll('select[multiple]');

        multipleSelects.forEach((select, i) => {
            new Choices(select, {
                removeItemButton: true,
                placeholder: true,
                placeholderValue: select.getAttribute('data-placeholder') || '',
                itemSelectText: 'Klicka för att välja',
                noChoicesText: 'Det finns inga alternativ att välja på.',
                noResultsText: 'Inga resultat.',
                loadingText: 'Laddar...',
                /*	callbackOnInit: () => {
                    const outerContainer = document.querySelector('[data-type="select-multiple"]');
                    outerContainer.setAttribute('aria-controls', 'multi-select-' + i);
                },*/
                callbackOnCreateTemplates: function () {
                    return {
                        dropdown: classes => {
                            const div = document.createElement('div');
                            div.setAttribute('id', 'multiselect-' + i);
                            div.classList.add(classes.list, classes.listDropdown);
                            return div;
                        },
                        containerOuter: (
                            classes,
                            dir,
                            isSelectElement,
                            isSelectOneElement,
                            searchEnabled,
                            passedElementType
                        ) => {
                            const div = Object.assign(document.createElement('div'), { className: classes.containerOuter });

                            div.dataset.type = passedElementType;
                            //	div.setAttribute('aria-controls', 'multiselect-' + i);
                            div.setAttribute('aria-haspopup', 'true');
                            div.setAttribute('aria-expanded', 'false');
                            div.setAttribute('aria-labelledby', 'multiselect-label-' + i);

                            if (dir) {
                                div.dir = dir;
                            }

                            if (isSelectElement) {
                                div.setAttribute('role', searchEnabled ? 'combobox' : 'listbox');
                                if (searchEnabled) {
                                    div.setAttribute('aria-autocomplete', 'list');
                                }
                            }

                            if (isSelectOneElement) {
                                div.tabIndex = 0;
                            }

                            return div;
                        },
                        choiceList(classes, isSelectOneElement) {
                            const div = Object.assign(document.createElement('div'), { className: classes.list });
                            div.setAttribute('role', 'listbox');
                            div.setAttribute('aria-labelledby', 'multiselect-label-' + i);
                            if (!isSelectOneElement) {
                                div.setAttribute('aria-multiselectable', 'true');
                            }
                            return div;
                        },
                    };
                },
            });

            select.addEventListener('removeItem', () => this._validateField(select), false);
            select.addEventListener('addItem', () => this._validateField(select), false);
        });
    }

    /**
      * Initiates events.
      * @private
      */
    _initEvents() {
        // Field validation on blur
        this.formNode.addEventListener('focusout', event => {

            let input = event.target;

            if (!input.matches('input, textarea, select')) {
                return;
            }

            // ??
            if (input.matches('.choices__input')) {
                input = input.parentNode.querySelector('select');
            }

            if (input.matches('fieldset *')) {
                input = input.closest('fieldset');
            }

            this._validateField(input);
        });

        // Submit
        this.formNode.addEventListener('submit', event => {
            if (!this._validateForm()) {
                event.preventDefault();
                return false;
            }

            // If the user submits with Enter press we need to handle multistep forms.
            const currentNextButton = this._getCurrentNextButton();

            if (currentNextButton) {
                this._submitStep(currentNextButton.getAttribute('aria-controls'));
                event.preventDefault();
                return false;
            }

            if (FormData) {
                if (event.preventDefault) {
                    event.preventDefault();
                } else {
                    event.returnValue = false;
                }

                const submitBtn = this.formNode.querySelector('[type="submit"]');
                if (submitBtn) {
                    this._displayLoadingIndicator(submitBtn);
                }

                this._submit();
            }
        });

        // Multistep forms
        this.formNode.addEventListener('click', event => {
            if (!event.target.hasAttribute('aria-controls')) {
                return;
            }

            const btn = event.target;

            if (btn.hasAttribute('data-epiform-previous')) {
                const currentStepIndexField = this.formNode.querySelector('[name="__FormCurrentStepIndex"]');
                if (currentStepIndexField) {
                    currentStepIndexField.value = parseInt(currentStepIndexField.value, 10) - 1;
                }
                this._showStep(btn.getAttribute('aria-controls'));
                return false;
            }

            if (FormData) {
                this._cleanupOldStatusMessages();
                if (btn.classList.contains('form__button--prev') || this._stepIsValid()) {
                    this._displayLoadingIndicator(btn);
                    this._submitStep(btn.getAttribute('aria-controls'));
                }
            } else {
                this.formNode.submit();
            }
        });

        // reset av formulär
        this.formNode.addEventListener('reset', () => this._reset());
    }

    /**
     * Function that submits the form through ajax.
     * @private
     */
    _submit(stepData, stepId) {

        if (this.requestInProgress === true) {
            return;
        }

        if (typeof grecaptcha === 'undefined') {
            return;
        }

        this.requestInProgress = true;

        const method = this.formNode.getAttribute('method') || 'POST';
        const url = this.formNode.getAttribute('action');
        const formData = stepData || new FormData(this.formNode);

        const siteKey = document.body.dataset.recaptchaSiteKey || '';

        this._clearValidationErrors();

        grecaptcha.ready(() => {
            grecaptcha.execute(siteKey, { action: 'form_submit' })
                .then(token => {

                    if (formData instanceof FormData) {
                        formData.set('__FormRecaptchaToken', token);
                    } else {
                        formData['__FormRecaptchaToken'] = token;
                    }

                    return axios({ url, method, data: formData });
                })
                .then(response => {
                    const data = response.data;

                    if (!data.isSuccess) {
                        return Promise.reject(data);
                    }

                    if (data.redirectUrl) {
                        window.location.replace(data.redirectUrl);
                        return;
                    }

                    // Update hidden fields if needed
                    if (stepId) {
                        const stepData = data.data;
                        if (stepData && stepData.currentStepIndexField) {
                            for (const key in stepData) {
                                if (stepData.hasOwnProperty(key)) {
                                    const field = this.formNode.querySelector(`[name="__Form${key}"]`);
                                    if (field) {
                                        field.value = stepData[key];
                                    }
                                }
                            }
                        }
                    }

                    if (stepId) {
                        this._showStep(stepId);
                    } else if (this.formMessageNode && data.message) {
                        this._displayMessage(data);
                        this.formNode.dispatchEvent(new Event('epiformsubmitted', { bubbles: true }));
                    }
                })
                .catch(data => {
                    if (data && data.isSuccess === false) {
                        this._handleServerValidationErrors(data);
                    } else {
                        alert(this.formNode.getAttribute('data-server-error-message') || 'Ett fel inträffade, vänligen försök igen senare.');
                    }
                })
                .then(() => {
                    this.requestInProgress = false;
                    this._hideLoadingIndicator();
                });
        });
    }

    /**
     * Removes validation invalid classes and validation error labels.
     * @private
     */
    _clearValidationErrors() {
        // ta bort klasser från fält
        for (const field of this.formNode.querySelectorAll('.' + this.invalidFieldClass)) {
            field.classList.remove(this.invalidFieldClass);
        }
        // ta bort error-labels
        for (const label of this.formNode.querySelectorAll('.' + this.invalidLabelClass)) {
            label.remove();
        }
    }

    /**
     * Returns the label node for the provided field
     * @param {Node} field - The field node
     * @private
     */
    _getFieldLabelNode(field) {

        // ??
        if (field.matches('.choices__input')) {
            return field.parentNode.parentNode.parentNode.querySelector('label');
        }

        if (field.matches('fieldset')) {
            return field.querySelector('legend');
        }

        // Hämtar label som omsluter input, eller label med matchande for-id, eller slutligen första bästa label inom överordnad nod
        return field.closest('label') || (field.id && this.formNode.querySelector(`label[for="${field.id}"]`)) || field.parentNode.querySelector('label');
    }

    /**
     * Handles serverside validation errors.
     * @param {Object} data - Form response data.
     * @private
     */
    _handleServerValidationErrors(data) {

        const fields = data && data.additionalParams && data.additionalParams.__FormField;

        if (!(fields && fields.length)) {
            return;
        }

        let summaryHtml = '';
        let index = 0;

        for (const field of fields) {

            summaryHtml += `<li><strong>${field.invalidElementLabel}</strong><span class="${this.invalidLabelClass}">${field.validationMessage}</span></li>`;

            const fieldNode = document.getElementById(field.invalidElement);

            if (!fieldNode) {
                continue;
            }

            fieldNode.classList.add(this.invalidFieldClass);

            if (fieldNode.type === 'hidden') {
                continue;
            }

            if (field.validationMessage) {
                this._showFieldValidationError(fieldNode, field.validationMessage);

                //if (fieldNode.matches('fieldset') && index === 0) {
                //    errorNode.focus();
                //}

                //const errorNode = this._createErrorNode(this.invalidFieldClass, field.validationMessage);
                //if (fieldNode.matches('fieldset')) {
                //    const legend = fieldNode.querySelector('legend');
                //    if (legend) {
                //        legend.appendChild(errorNode);
                //        errorNode.setAttribute('tabindex', -1);
                //        if (index === 0) {
                //            errorNode.focus();
                //        }
                //    }
                //} else {
                //    const fieldLabel = this.formNode.querySelector(`[for="${field.InvalidElement}"]`);
                //    fieldLabel && fieldLabel.appendChild(errorNode);
                //}
            }

            if (index === 0) {
                //if (!fieldNode.matches('fieldset')) {
                //    fieldNode.focus();
                //}

                fieldNode.focus();

                if (fieldNode.matches('input[type=text], textarea') && fieldNode.selectionStart >= 0) {
                    // Set cursor at the end of the fields value.
                    const len = fieldNode.value.length;
                    fieldNode.selectionStart = len;
                    fieldNode.selectionEnd = len;
                    fieldNode.focus();
                }
            }

            index++;
        }

        this._displayValidationErrorSummary(fields.length, '<ul>' + summaryHtml + '</ul>');
    }

    /**
     * Displays a validation error summary above the form.
     * @param {Number} nrOfErrors - Number of fields that has validation errors.
     * @param {String} fieldsListHTML - String of HTML containing a ul-list with invalid field labels.
     * @private
     */
    _displayValidationErrorSummary(nrOfErrors, fieldsListHTML) {
        this._cleanupOldStatusMessages();

        if (!this.formMessageNode) {
            return;
        }

        const errorMessage = this.formNode.getAttribute('data-validation-error-message') || 'Formuläret innehåller {0} fel:';

        const headlineNode = document.createElement('h2');
        headlineNode.innerHTML = errorMessage.replace('{0}', nrOfErrors);

        const responseMessageNode = this._createMessageNode();
        responseMessageNode.appendChild(headlineNode);
        responseMessageNode.innerHTML += fieldsListHTML;
        this.formMessageNode.appendChild(responseMessageNode);

        responseMessageNode.focus();
    }

    /**
     * Cleans up old status messages
     * @private
     */
    _cleanupOldStatusMessages() {
        this.formMessageNode.querySelectorAll('.form__status').forEach(status => remove(status));
    }

    /**
     * Creates a div message node and sets correct classes.
     * @param {Object} data - Form response data.
     * @private
     */
    _createMessageNode(data) {
        const statusClass = data && data.isSuccess ? 'form__status--success' : 'form__status--error';

        const sumNode = document.createElement('div');
        sumNode.setAttribute('tabindex', '-1');
        sumNode.classList.add('form__status', 'form__rte', statusClass);

        return sumNode;
    }

    /**
     * Displays a message above the form.
     * @param {Object} data - Form response data.
     * @private
     */
    _displayMessage(data) {
        const messageNode = this._createMessageNode(data);
        messageNode.innerHTML = data.message;

        this._clearMessageNode();
        this.formMessageNode.appendChild(messageNode);

        this.fieldHolderNode.classList.add('form__fields-holder--hidden');

        messageNode.focus();
    }

    /**
     * 
     * */
    _clearMessageNode() {
        // Remove all content in formMessageNode and adds new node
        while (this.formMessageNode.firstChild) {
            this.formMessageNode.lastChild.remove();
        }
    }

    /**
     * 
     * */
    _reset() {
        this._clearMessageNode();
        //this.formNode.reset();    
        this.fieldHolderNode.classList.remove('form__fields-holder--hidden');
    }

    /**
     * Submits a step in a multi step form.
     * @param {String} stepId - The id of the step section node.
     * @private
     */
    _submitStep(stepId) {
        const currentStep = this.formNode.querySelector('.form__section[aria-hidden="false"]');

        if (!currentStep) {
            return;
        }

        let formData = this._appendHiddenFieldValues(new FormData());

        const currentStepFields = currentStep.querySelectorAll('input, select, textarea');

        currentStepFields.forEach(field => {
            if (!field.classList.contains('choices__input--cloned')) {
                formData = this._appendFieldValue(field, formData);
            }
        });

        this._submit(formData, stepId);
    }

    /**
     * Extracts values from hidden fields and appends to FormData object
     * @param {FormData} formData - Current form data
     * @returns {FormData}
     * @private
     */
    _appendHiddenFieldValues(formData) {
        const hiddenFieldHolder = this.formNode.querySelector('.form__hidden-fields');

        if (hiddenFieldHolder) {
            for (const field of hiddenFieldHolder.querySelectorAll('input, select')) {
                formData.append(field.getAttribute('name'), field.value);
            }
        }

        return formData;
    }

    /**
     * Extracts value from the given field and appends to FormData object
     * @param {Node} field - The current field node
     * @param {FormData} formData - Current form data
     * @returns {FormData}
     * @private
     */
    _appendFieldValue(field, formData) {

        if (field.options) {
            // Selects
            for (const opt of field.options) {
                if (opt.selected) {
                    formData.append(field.name, opt.value);
                }
            }
        } else {
            // Checkbox and radio
            switch (field.type) {
                case 'checkbox':
                case 'radio':
                    if (field.checked) {
                        formData.append(field.name, field.value);
                    }
                    break;
                case 'file':
                    for (const file of field.files) {
                        formData.append(field.name, file);
                    }
                    break;
                default:
                    formData.append(field.name, field.value);
                    break;
            }
        }

        return formData;
    }

    /**
     * Validates a step in a multi step form.
     * @private
     */
    _stepIsValid() {
        const step = this.formNode.querySelector('.form__section[aria-hidden="false"]');
        const fields = !step ? [] : step.querySelectorAll('input, select, fieldset');
        return this._validateForm(fields);
    }

    /**
     * Shows a step in a multi step form.
     * @param {String} stepId - The id of the step section node.
     * @private
     */
    _showStep(stepId) {
        const stepButtons = this.formNode.querySelectorAll('[aria-controls]');
        for (const btn of stepButtons) {
            btn.setAttribute('aria-expanded', false);
        }

        const steps = this.formNode.querySelectorAll('.form__section');
        for (const step of steps) {
            step.setAttribute('aria-hidden', true);
            step.classList.add('form__section--hidden');
        }

        const nextStep = document.getElementById(stepId);
        nextStep.setAttribute('aria-hidden', false);
        nextStep.classList.remove('form__section--hidden');

        const nextStepButton = this.formNode.querySelector('[aria-controls=' + stepId + ']');
        nextStepButton.setAttribute('aria-expanded', true);

        const nextStepHeadline = nextStep.querySelector('.form__section__headline');
        if (nextStepHeadline) {
            nextStepHeadline.focus();
        }
    }

    /**
     * Displays a spinner and a message in button.
     * @param {Object} btn - A button node.
     * @private
     */
    _displayLoadingIndicator(btn) {
        const loadingMessage = btn.getAttribute('data-loading-message') || 'Laddar...';
        btn.classList.add('form__button--loading');
        btn.setAttribute('data-innerhtml', btn.innerHTML);
        btn.innerHTML = '<span class="form__loader" aria-hidden="true"></span> ' + loadingMessage;
    }

    /**
     * Hides the spinner and message in buttons and restores their innerHTML.
     * @private
     */
    _hideLoadingIndicator() {
        for (const btn of this.formNode.querySelectorAll('.form__button--loading')) {
            btn.innerHTML = btn.getAttribute('data-innerhtml');
        }
    }

    /**
     * Returns the next button for the current step.
     * @private
     */
    _getCurrentNextButton() {
        const buttons = this.formNode.querySelectorAll('.form__button--next');

        // returnera sista i listan?
        for (const i = buttons.length - 1; 0 <= i; i--) {
            if (buttons[i].offsetParent) {
                return buttons[i];
            }
        }
    }

    /**
     * Checks if all fields with requirements (required, pattern) are valid before the form is submitted
     * @param {Array} fields - If we're validating a multistep form, the function is provided with the fields array
     * @private
     */
    _validateForm(fields) {

        if (fields == null) {
            fields = this.formNode.querySelectorAll('input, textarea, select, fieldset');
        }

        const errors = [];

        for (const field of fields) {
            if (this._validateField(field) === false) {
                const label = this._getFieldLabelNode(field);
                errors.push('<li><strong>' + label.innerText + '</strong></li>\n');
            }
        }

        if (errors.length) {
            const html = '<ul>' + errors.join('') + '</ul>';
            this._displayValidationErrorSummary(errors.length, html);
        }

        return errors.length === 0;
    }

    /**
     * Checks if a field has validation errors, if so, renders a validation error message
     * @param {Node} input - The field node to validate
     * @private
     * @returns true or false if the field require validation, else null
     */
    _validateField(input) {

        let isValid = null;
        let error = null;

        // obligatoriska fält, text eller checkbox-/radiolista
        if (input.required || input.matches('[data-required="true"]')) {

            if (input.type === 'checkbox' || input.type === 'radio') {
                isValid = input.checked;
            } else if (input.matches('fieldset')) {
                isValid = input.querySelector('input:checked');
            } else if (input.matches('select[multiple]')) {
                isValid = input.querySelector('option:checked');
            } else {
                isValid = input.value;
            }

            isValid = !!(isValid);

            if (!isValid) {
                error = input.getAttribute('data-validation-message') || 'Du måste fylla i det här fältet för att gå vidare.';
            }
        } else if (input.type === 'url' && !input.checkValidity()) {
            // URL-fält
            isValid = false;
            error = input.getAttribute('data-validation-message') || 'Ange en giltig URL.';
        } else if (input.pattern && input.value && !input.value.match(input.pattern)) {
            // formatterad
            isValid = false;
            error = input.getAttribute('data-pattern-message') || 'Vänligen matcha det förväntade formatet.';
        }

        if (isValid != null) {
            this._clearFieldValidationErrors(input);

            if (isValid === false) {
                this._showFieldValidationError(input, error);
            }
        }

        return isValid;
    }

    /**
     * Writes out a validation error message in field label tag and sets field as invalid.
     * @param {Node} input - The input field node
     * @param {String} message - The validation message to display
     * @private
     */
    _showFieldValidationError(input, message) {

        if (input.matches('select[multiple]')) {
            input = input.parentNode.querySelector('input');
        }

        input.classList.add(this.invalidFieldClass);

        const errorNode = this._createErrorNode(this.invalidLabelClass, message);
        input.closest('.form__item').appendChild(errorNode);

        return errorNode;
    }

    /**
     * Cleans up validation error for a specific field
     * @param {Node} input - The field node
     * @private
     */
    _clearFieldValidationErrors(input) {
        // ??
        if (input.matches('select[multiple]')) {
            input = input.parentNode.querySelector('input');
        }

        input.classList.remove(this.invalidFieldClass);

        //const labelNode = this._getFieldLabelNode(input);
        const field = input.closest('.form__item');
        const errorNode = field && field.querySelector('.' + this.invalidLabelClass);
        errorNode && errorNode.remove();
    }

    /**
     * Creates a <span> with specified class and text
     * @param {any} cssClass
     * @param {any} text
     * @returns
     */
    _createErrorNode(cssClass, text) {
        const node = document.createElement('span');
        node.classList.add(cssClass);
        node.appendChild(document.createTextNode(text));
        node.setAttribute('tabindex', -1);
        return node;
    }
}
